import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  FilterList as FilterIcon,
  AutoAwesome as AutoMatchIcon,
  ArrowUpward as EntradaIcon,
  ArrowDownward as SaidaIcon,
  CheckCircle as CheckCircleIcon,
  PostAdd as PostAddIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const MatchingPanelHeader = ({
  selectedStatement,
  transactionsCount,
  journalEntriesCount,
  transactionTypeFilter,
  onTransactionTypeFilterChange,
  reconciliationFilter,
  onReconciliationFilterChange,
  onAutoMatch,
  isAutoMatching,
  showAutoMatch = true,
  canReconcile,
  onReconcile,
  canBulkCreate,
  onBulkCreateEntries,
  selectedTransactionsCount,
  canBulkReconcile,
  onBulkReconcile,
}) => {
  const theme = useTheme();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(parseISO(dateString), 'dd/MM/yy', { locale: ptBR });
    } catch {
      return '';
    }
  };

  return (
    <Box
      sx={{
        flexShrink: 0,
        p: 1,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
        {/* Left: Statement name + dates */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
          <BankIcon fontSize="small" />
          <Box>
            <Typography variant="body2" fontWeight="700" sx={{ lineHeight: 1.2 }}>
              {selectedStatement.fileName}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, lineHeight: 1 }}>
              {selectedStatement.bankAccount?.name} • {formatDate(selectedStatement.statementStartDate)} - {formatDate(selectedStatement.statementEndDate)}
            </Typography>
          </Box>
        </Stack>

        {/* Center: Reconcile / Bulk Create button */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
          {canBulkCreate && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PostAddIcon fontSize="small" />}
              onClick={onBulkCreateEntries}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                px: 2,
                height: 34,
                fontWeight: 700,
                backgroundColor: 'white',
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha('#ffffff', 0.9),
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                },
              }}
            >
              Criar {selectedTransactionsCount} Lançamentos
            </Button>
          )}
          {canBulkReconcile && (
            <Button
              variant="contained"
              size="small"
              startIcon={<CheckCircleIcon fontSize="small" />}
              onClick={onBulkReconcile}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                px: 2,
                height: 34,
                fontWeight: 700,
                backgroundColor: 'white',
                color: theme.palette.success.main,
                boxShadow: `0 0 14px ${alpha(theme.palette.success.main, 0.4)}`,
                '&:hover': {
                  backgroundColor: alpha('#ffffff', 0.9),
                  boxShadow: `0 4px 16px ${alpha(theme.palette.success.main, 0.5)}`,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Conciliar 1:1 ({selectedTransactionsCount} pares)
            </Button>
          )}
          {canReconcile && (
            <Button
              variant="contained"
              size="small"
              startIcon={<CheckCircleIcon fontSize="small" />}
              onClick={onReconcile}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                px: 2,
                height: 34,
                fontWeight: 700,
                backgroundColor: theme.palette.success.main,
                color: 'white',
                boxShadow: `0 0 14px ${alpha(theme.palette.success.main, 0.6)}`,
                '&:hover': {
                  backgroundColor: theme.palette.success.dark,
                  boxShadow: `0 4px 16px ${alpha(theme.palette.success.main, 0.7)}`,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Conciliar
            </Button>
          )}
        </Stack>

        {/* Right: Filters */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ flex: 1, justifyContent: 'flex-end' }}>
            <Tooltip title="Filtrar apenas entradas" arrow>
              <Chip
                icon={<EntradaIcon sx={{ fontSize: '0.9rem' }} />}
                label="Entradas"
                size="small"
                clickable
                onClick={() => onTransactionTypeFilterChange('entrada')}
                sx={{
                  height: 26,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  border: `1px solid ${
                    transactionTypeFilter === 'entrada'
                      ? '#ffffff'
                      : 'rgba(255,255,255,0.8)'
                  }`,
                  background: transactionTypeFilter === 'entrada'
                    ? `linear-gradient(135deg, ${alpha(theme.palette.success.light, 0.95)}, ${theme.palette.success.dark})`
                    : `linear-gradient(135deg, ${alpha('#ffffff', 0.15)}, ${alpha('#ffffff', 0.08)})`,
                  color: transactionTypeFilter === 'entrada' ? 'white' : 'rgba(255,255,255,0.9)',
                  boxShadow: transactionTypeFilter === 'entrada'
                    ? `0 0 12px ${alpha(theme.palette.success.main, 0.6)}`
                    : `0 0 0 0 transparent`,
                  transform: transactionTypeFilter === 'entrada' ? 'translateY(-1px) scale(1.02)' : 'none',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.light, 0.95)}, ${theme.palette.success.main})`,
                    boxShadow:
                      transactionTypeFilter === 'entrada'
                        ? `0 0 14px ${alpha(theme.palette.success.main, 0.6)}`
                        : `0 0 6px rgba(255,255,255,0.4)`,
                  },
                  transition: 'all 0.2s ease-in-out, transform 0.2s ease',
                }}
              />
            </Tooltip>
          <Tooltip title="Filtrar apenas saídas" arrow>
            <Chip
              icon={<SaidaIcon sx={{ fontSize: '0.9rem' }} />}
              label="Saídas"
              size="small"
              clickable
              onClick={() => onTransactionTypeFilterChange('saida')}
              sx={{
                height: 26,
                fontSize: '0.7rem',
                fontWeight: 500,
                border: `1px solid ${
                  transactionTypeFilter === 'saida'
                    ? '#ffffff'
                    : 'rgba(255,255,255,0.8)'
                }`,
                background: transactionTypeFilter === 'saida'
                  ? `linear-gradient(135deg, ${alpha(theme.palette.error.light, 0.95)}, ${theme.palette.error.dark})`
                  : `linear-gradient(135deg, ${alpha('#ffffff', 0.15)}, ${alpha('#ffffff', 0.08)})`,
                color: transactionTypeFilter === 'saida' ? 'white' : 'rgba(255,255,255,0.9)',
                boxShadow: transactionTypeFilter === 'saida'
                  ? `0 0 12px ${alpha(theme.palette.error.main, 0.6)}`
                  : `0 0 0 0 transparent`,
                transform: transactionTypeFilter === 'saida' ? 'translateY(-1px) scale(1.02)' : 'none',
                '&:hover': {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.error.light, 0.95)}, ${theme.palette.error.main})`,
                  boxShadow:
                    transactionTypeFilter === 'saida'
                      ? `0 0 14px ${alpha(theme.palette.error.main, 0.6)}`
                      : `0 0 6px rgba(255,255,255,0.4)`,
                },
                transition: 'all 0.2s ease-in-out, transform 0.2s ease',
              }}
            />
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={reconciliationFilter}
              onChange={(e) => onReconciliationFilterChange(e.target.value)}
              displayEmpty
              sx={{
                fontSize: '0.75rem',
                height: 34,
                backgroundColor: 'white',
                color: 'black',
                '& .MuiSelect-select': {
                  py: 0.75,
                  px: 1.5,
                },
              }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterIcon fontSize="small" />
                  Todos
                </Box>
              </MenuItem>
              <MenuItem value="unreconciled" sx={{ fontSize: '0.75rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterIcon fontSize="small" />
                  Não Conciliados
                </Box>
              </MenuItem>
              <MenuItem value="reconciled" sx={{ fontSize: '0.75rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterIcon fontSize="small" />
                  Conciliados
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          {showAutoMatch && (
            <Tooltip title="Buscar correspondências automáticas" arrow>
              <Button
                variant="contained"
                size="small"
                startIcon={<AutoMatchIcon fontSize="small" />}
                onClick={onAutoMatch}
                disabled={isAutoMatching}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  px: 1.5,
                  height: 34,
                  fontWeight: 600,
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                Auto Match
              </Button>
            </Tooltip>
          )}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              label={`${journalEntriesCount} lançamentos`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
              }}
            />
            <Chip
              label={`${transactionsCount} transações no extrato`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
              }}
            />
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default MatchingPanelHeader;